import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  getBulkInviteRequestDetails,
  inspectBulkInviteMapping,
  listBulkInviteRequests,
  reuploadBulkInvite,
  sendBulkInvite,
  validateBulkInvite,
} from '../bulk-invitations'
import { queryKeys } from '../react-query-config'
import type {
  BulkInviteListFilters,
  BulkInviteMappingInspection,
  BulkInviteMappingOverride,
  BulkInvitePreview,
  BulkInviteQueued,
  BulkInviteRequest,
  BulkInviteRequestsResponse,
  BulkInviteRowDetailsFilters,
  BulkInviteRowDetailsResponse,
  BulkInviteStatus,
} from '../types'

/** A file plus the admin's optional mapping override and removed rows — the shape every write takes. */
export interface BulkInviteSubmission {
  file: File
  mapping?: BulkInviteMappingOverride
  /** Row numbers the admin removed on the review screen — excluded from the send. */
  skipRows?: number[]
}

const TERMINAL_STATUSES: BulkInviteStatus[] = ['SUCCEEDED', 'PARTIAL', 'FAILED']

/** A request is still being worked on while it is QUEUED or PROCESSING. */
export const isBulkInviteActive = (status?: BulkInviteStatus): boolean =>
  !!status && !TERMINAL_STATUSES.includes(status)

/**
 * Realtime BULK_INVITE_PROGRESS frames patch these caches in place and
 * BULK_INVITE_COMPLETED refreshes them, so this slow poll is only a fallback
 * for a dropped websocket.
 */
const FALLBACK_POLL_INTERVAL_MS = 30000

/**
 * Inspect the file's column mapping — the cheap dry run behind the Review Column
 * Mapping screen. Deliberately a mutation, not a query: it runs once per uploaded
 * file, and the screen's edits are then client-side state. Do not re-run it per edit.
 */
export const useInspectBulkInviteMapping = () => {
  return useMutation<BulkInviteMappingInspection, Error, File>({
    mutationFn: inspectBulkInviteMapping,
    retry: false,
  })
}

/** Validate a file (full dry run) — returns the breakdown, sends nothing. */
export const useValidateBulkInvite = () => {
  return useMutation<BulkInvitePreview, Error, BulkInviteSubmission>({
    mutationFn: ({ file, mapping }) => validateBulkInvite(file, mapping),
    retry: false,
  })
}

/**
 * The QUEUED record to render from immediately, built from a 202 response. The
 * server hasn't told us the initiator or filename yet — the first fetch fills
 * those in, so they stay honestly empty rather than guessed.
 */
const seedFromQueued = (data: BulkInviteQueued, fileName: string | null): BulkInviteRequest => ({
  id: data.requestId,
  status: data.status,
  totalRows: data.totalRows,
  successCount: 0,
  failureCount: 0,
  skippedCount: 0,
  emailsNotSent: 0,
  fileName,
  initiator: null,
  requestedStorageBytes: null,
  requestedApiQuota: null,
  errors: null,
  skipped: null,
  createdAt: null,
  completedAt: null,
})

/**
 * Send a file (async). Seeds the detail cache with the initial QUEUED state from
 * the 202 response so the tracking view renders immediately without a network
 * round-trip, then invalidates the history list so the new request appears.
 */
export const useSendBulkInvite = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkInviteQueued, Error, BulkInviteSubmission>({
    mutationFn: ({ file, mapping, skipRows }) => sendBulkInvite(file, mapping, skipRows),
    retry: false,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<BulkInviteRequest>(
        queryKeys.bulkInvitations.detail(data.requestId),
        seedFromQueued(data, variables.file.name),
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.bulkInvitations.lists() })
    },
  })
}

/**
 * Re-upload a corrected file into an existing request. The record is overwritten in
 * place and re-processed under the SAME requestId, so this reseeds that request's
 * caches rather than creating a new one — the modal keeps tracking the same id.
 *
 * The row details of the previous attempt are stale the moment this succeeds, so they
 * are invalidated too; successes already invited under this request survive on the
 * server and are recounted at finalize.
 */
export const useReuploadBulkInvite = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkInviteQueued, Error, BulkInviteSubmission & { requestId: string }>({
    mutationFn: ({ requestId, file, mapping, skipRows }) =>
      reuploadBulkInvite(requestId, file, mapping, skipRows),
    retry: false,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<BulkInviteRequest>(
        queryKeys.bulkInvitations.detail(data.requestId),
        old =>
          // Keep what the server already told us about this request (initiator,
          // createdAt); only the counts and status restart.
          old
            ? {
                ...old,
                status: data.status,
                totalRows: data.totalRows,
                successCount: 0,
                failureCount: 0,
                skippedCount: 0,
                emailsNotSent: 0,
                fileName: variables.file.name,
                errors: null,
                skipped: null,
                completedAt: null,
              }
            : seedFromQueued(data, variables.file.name),
      )
      // Prefix without the filters slot, so every paginated variant of this
      // request's details is invalidated — not just the unfiltered one.
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.bulkInvitations.rowDetailsList(), data.requestId],
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.bulkInvitations.lists() })
    },
  })
}

/**
 * Track a single request. The 202 response seeds the detail cache immediately
 * (via useSendBulkInvite), so this hook has data before any network call.
 * Realtime progress/completion events keep it fresh; the slow fallback poll
 * runs only while QUEUED/PROCESSING and stops at a terminal status. The
 * single-request status endpoint is gone — the queryFn reads from the list.
 */
export const useBulkInviteRequest = (
  requestId: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery<BulkInviteRequest, Error>({
    queryKey: queryKeys.bulkInvitations.detail(requestId || ''),
    queryFn: async () => {
      const response = await listBulkInviteRequests({ limit: 100 })
      const found = response.data.find(r => r.id === requestId)

      if (!found) throw new Error('Bulk invite request not found')

      return found
    },
    enabled: !!requestId && options?.enabled !== false,
    refetchInterval: query =>
      isBulkInviteActive(query.state.data?.status) ? FALLBACK_POLL_INTERVAL_MS : false,
  })
}

/**
 * Per-row outcomes of a single request (every file row merged: successes from
 * UM_Invitations, failures and pre-dispatch skips from errorReport). Sorted by
 * email, paginated. Powers the expanded Details view in the History tab.
 */
export const useBulkInviteRequestDetails = (
  requestId: string | undefined,
  filters: BulkInviteRowDetailsFilters = {},
  options?: { enabled?: boolean },
) => {
  return useQuery<BulkInviteRowDetailsResponse, Error>({
    queryKey: queryKeys.bulkInvitations.rowDetails(requestId || '', filters),
    queryFn: () => getBulkInviteRequestDetails(requestId as string, filters),
    enabled: !!requestId && options?.enabled !== false,
    placeholderData: previousData => previousData,
  })
}

/**
 * Tenant-wide history list — includes other admins' uploads, and is open to super
 * admins too (scoped by the x-tenant-id header the axios interceptor always sends).
 * Realtime events keep the rows fresh; the slow fallback poll runs while any
 * listed request is still active.
 */
export const useBulkInviteRequests = (
  filters: BulkInviteListFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery<BulkInviteRequestsResponse, Error>({
    queryKey: queryKeys.bulkInvitations.list(filters),
    queryFn: () => listBulkInviteRequests(filters),
    enabled: options?.enabled !== false,
    placeholderData: previousData => previousData,
    refetchInterval: query =>
      query.state.data?.data?.some(request => isBulkInviteActive(request.status))
        ? FALLBACK_POLL_INTERVAL_MS
        : false,
  })
}
